import React from 'react';
import { Button } from '@ygtechnologies/rubus-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSwatchbook } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';

import theme from './StyleSelectionGate.module.scss';
import { StyleSelectionModal } from './StyleSelectionModal';
import { useStyleWalkthrough } from './useStyleWalkthrough';

interface StyleSelectionGateProps {
    children: React.ReactNode;
}

/**
 * Gates the room's option menu behind style selection: when a required style group has no pick, the
 * menu (`children`) is replaced by a prompt asking the user to choose a style first. Clicking the CTA
 * opens the sequential style walkthrough; once the required styles are picked the menu is revealed.
 *
 * The prompt stays mounted while the walkthrough is open (even after the last required pick flips
 * `pending` off) so the modal isn't torn down mid-sequence — it closes on the walkthrough's own Done.
 */
export const StyleSelectionGate: React.FC<StyleSelectionGateProps> = ({ children }) => {
    const { pending, walkGroups, start, close } = useStyleWalkthrough();
    const walking = !!walkGroups && walkGroups.length > 0;

    if (!pending && !walking) {
        return <>{children}</>;
    }

    return (
        <div className={theme.gate}>
            <div className={theme.prompt}>
                <FontAwesomeIcon className={theme.icon} icon={faSwatchbook} />
                <h2 className={theme.title}>
                    <Trans>Choose your style</Trans>
                </h2>
                <p className={theme.text}>
                    <Trans>
                        Select a style for this room before you start choosing products. Your style
                        sets the look and narrows the available options.
                    </Trans>
                </p>
                <Button variant="primary" onClick={start}>
                    <Trans>Select style</Trans>
                </Button>
            </div>
            {walkGroups && walkGroups.length > 0 && <StyleSelectionModal groups={walkGroups} onClose={close} />}
        </div>
    );
};
